import { useSelector } from 'react-redux';

import React, { useMemo } from 'react';
import { Badge, Typography, TypographyVariantsEnum, IconGiftNew } from '@mindly/ui-components';
import { useTranslation } from 'react-i18next';
import { useTimer } from 'react-timer-hook';

import { selectUnUsedPromoCode } from '../../redux/promoCodes/promoCodes.selectors';
import { LightningIcon } from 'src/shared/assets/icons/lightningIcon';
import classNames from 'classnames';
import { removePromoCode } from 'src/redux/promoCodes/promoCodes.reducer';
import { useDispatch } from 'react-redux';

type PromoCodeTimerProps = {
  className?: string;
  endDate?: number;
  percentOff?: number | null;
  expandWidth?: boolean;
  endContent?: React.ReactNode;
};

const Timer = ({ endDate, onExpire }: { endDate: number; onExpire: () => void }) => {
  const { hours, minutes, seconds } = useTimer({
    expiryTimestamp: new Date(endDate),
    onExpire,
  });

  return (
    <div className="flex items-center px-4 py-1 bg-color-white rounded-full gap-1">
      <LightningIcon />
      <Typography variant={TypographyVariantsEnum.Text15Semi}>
        {hours ? `${hours}:` : ''}
        {minutes < 10 ? `0${minutes}` : minutes}:{seconds < 10 ? `0${seconds}` : seconds}
      </Typography>
    </div>
  );
};

export const PromoCodeTimerFeature: React.FC<PromoCodeTimerProps> = ({
  endDate: endDateProp,
  percentOff: percentOffProp,
  className,
  expandWidth = false,
  endContent,
}) => {
  const { t } = useTranslation('specialists');
  const unUsedPromoCode = useSelector(selectUnUsedPromoCode);
  const dispatch = useDispatch();

  const endDate = useMemo(() => {
    if (endDateProp) return endDateProp;
    if (!unUsedPromoCode) return 0;
    return unUsedPromoCode.end_date._seconds * 1000;
  }, [endDateProp, unUsedPromoCode]);

  const handleExpire = () => {
    dispatch(removePromoCode(unUsedPromoCode?.code));
  };

  const percentOff = useMemo(() => {
    if (percentOffProp) return percentOffProp;
    return unUsedPromoCode?.percent_off;
  }, [percentOffProp, unUsedPromoCode]);

  const isShown = useMemo(() => {
    return endDate && percentOff;
  }, [endDate, percentOff]);

  if (!isShown) {
    return null;
  }

  if (unUsedPromoCode && !unUsedPromoCode?.onboarding_key) {
    return (
      <Badge
        variant="info"
        type="default"
        className={classNames(
          'p-4 px-5 cursor-pointer rounded-none mb-3 flex items-center justify-center',
          { 'w-full box-content -ml-5': expandWidth },
          className
        )}
      >
        <IconGiftNew className="w-5 h-5 mr-1" />
        <Typography variant={TypographyVariantsEnum.Text15Semi}>
          {t('trial.discount.banner.title', { discount: `${percentOff}%` })}
        </Typography>
      </Badge>
    );
  }

  return (
    <Badge
      variant="info"
      type="default"
      className={classNames(
        'p-4 px-5 rounded-none flex items-center gap-3',
        {
          'w-full box-content -ml-5': expandWidth,
          'cursor-pointer': unUsedPromoCode && !endDateProp,
          'justify-center': !endContent,
          'justify-between py-2': endContent,
        },
        className
      )}
    >
      <div
        className={classNames('flex justify-center', {
          'flex-col gap-1 items-start': endContent,
          'gap-4 items-center': !endContent,
        })}
      >
        <Typography variant={endContent ? TypographyVariantsEnum.Text13Semi : TypographyVariantsEnum.Text15Semi}>
          {t('trial.discount.timer.title', { percentOff })}
        </Typography>

        <Timer endDate={endDate} onExpire={handleExpire} />
      </div>
      {endContent}
    </Badge>
  );
};
